import cookies from "@/config/cookies"
import { CookiesEnum } from "@/enums/cookies"
import { Locales } from "@/enums/locales"
import { CgosCompaniesEnum, CompaniesEnum } from "@/enums/companies"

export const getCookie = (name) => {
	if (!(typeof name === "string" || name instanceof String))
		throw new TypeError("Argument 1 must be a string")

	const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"))
	if (match) return match[2]
}

export const replaceCookie = (name) => {
	const currentPathArray = window.location.pathname.split("/")
	const filteredPathArray = currentPathArray.filter((item) => item)

	const cookiePath = `${filteredPathArray[0]}/${filteredPathArray[1]}`
	const currentCookieValue = getCookie(name)
	document.cookie =
		name + "=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/" + cookiePath + ";"
	const newCookieValue = getCookie(name)
	if (!newCookieValue) {
		document.cookie = `${name}=${currentCookieValue};path=/"`
		return
	}

	const newCookieLength = JSON.parse(decodeURIComponent(newCookieValue)).length;
	document.cookie = `${cookies.PARTICIPANTS_NUM}=${newCookieLength}`
	document.cookie = `${name}=${newCookieValue};path=/"`
}

export const formatDateForRequest = (dateStr) => {
	if (!(typeof dateStr === "string" || dateStr instanceof String))
		throw new TypeError("Argument 1 must be a string")
	// dateStr should be of format DD/MM/yyyy, just like we have in the URL hash; no need to make this func super smart
	try {
		if (!dateStr) return ""
		const [day, month, year] = dateStr.split("/")
		return `${year}-${month}-${day}`
	} catch (err) {
		console.error(err)
		return dateStr
	}
}

export const formatDateForHash = (dateStr, isDatePicker=false) => {
	if (!(typeof dateStr === "string" || dateStr instanceof String))
		throw new TypeError("Argument 1 must be a string")
	// dateStr should be of format yyyy-MM-DD, just like we receive in API responses; no need to make this func super smart
	try {
		if (!dateStr) return ""
		const [year, month, day] = dateStr.split("-")

		// Adjust format for DatePicker, which expects yyyy/MM/dd.
		if(isDatePicker) return `${year}/${month}/${day}`
		return `${day}/${month}/${year}`
	} catch (err) {
		console.error(err)
		return dateStr
	}
}


/**
 * Calculates the adjacent month based on a given starting month, direction, and jump value.
 * 
 * @param {string} month - The starting month as a string in the format "MM" (01 to 12).
 * @param {string} flag - The direction to move. Use 'next' to move forward or 'prev' to move backward.
 * @param {number} [jump=1] - The number of months to jump. Defaults to 1. Must be a positive integer.
 * 
 * @returns {string} - The resulting month as a string in the format "MM" (01 to 12).
 * 
 * @throws {Error} If the provided `month` is not a valid month (01 to 12).
 * @throws {Error} If the `jump` value is not a positive integer.
 * @throws {Error} If the `flag` is not 'next' or 'prev'.
 * 
 * @example
 * getAdjacentMonth('03', 'next', 2); // Returns '05'
 * getAdjacentMonth('12', 'next', 2); // Returns '02'
 * getAdjacentMonth('01', 'prev', 3); // Returns '10'
 * getAdjacentMonth('11', 'prev', 1); // Returns '10'
 */
export const getAdjacentMonth =(month, flag, jump = 1) =>{
  // Remove leading zeros by converting the month string to an integer
  const sanitizedMonth = parseInt(month, 10); // Parse month as a base-10 integer
	
  // Ensure the month is within valid range (1 to 12)
  if (sanitizedMonth < 1 || sanitizedMonth > 12) {
    throw new Error("Invalid month value");
  }

  // Ensure the jump is a positive integer
  if (jump < 1 || !Number.isInteger(jump)) {
    throw new Error("Jump value must be a positive integer");
  }

  let newMonth;
  if (flag === 'next') {
    // Calculate the new month by adding the jump value and wrapping around using modulo
    newMonth = (sanitizedMonth + jump - 1) % 12 + 1;
  } else if (flag === 'prev') {
    // Calculate the new month by subtracting the jump value and wrapping around using modulo
    newMonth = (sanitizedMonth - jump - 1 + 12) % 12 + 1;
  } else {
    throw new Error("Invalid flag value. Use 'next' or 'prev'.");
  }

  // Add a leading zero if the month is a single digit
  return newMonth.toString().padStart(2, '0');
}

export const getDeepCopy = (obj) => {
	if (!obj) return
	if (!(obj instanceof Object))
		throw new TypeError("Argument 1 must be an Object")
	return JSON.parse(JSON.stringify(obj))
}

export const getHashObj = (hashStr) => {
	if (!(typeof hashStr === "string" || hashStr instanceof String))
		throw new TypeError("Argument 1 must be a string")

	const hashObj = {}
	if (hashStr) {
		const keyValuePairs = hashStr.replace("#", "").split("&")
		keyValuePairs.forEach((pair) => {
			const [key, value] = pair.split("=")
			hashObj[key] = value
		})
	}
	return hashObj
}

export const setHash = (newEntries) => {
	if (!(newEntries instanceof Object))
		throw new TypeError("Argument 1 must be an Object")
	const hash = window.location.hash.replace("#", "")

	const existingHashObj = getHashObj(hash)

	const newHash = { ...existingHashObj, ...newEntries }

	let pairs = []
	Object.entries(newHash).forEach(([key, value]) => {
		pairs.push(`${key}=${value}`)
	})
	
	const newHashString = `#${pairs.join("&")}`
    history.replaceState(null, '', window.location.pathname + window.location.search + newHashString);
}

export const parseHashParams = () => {
	const hash = window.location.hash
	const hashString = hash.startsWith('#') ? hash.slice(1) : hash;
	const pairs = hashString.split('&');
	const result = {};
	pairs.forEach((pair) => {
	  const [key, value] = pair.split('=');
	  if (key) {
		result[key] = decodeURIComponent(value || '');
	  }
	});
	return result;
  };

export const elementInViewport = (element) => {
	if (!element) throw new Error("element cannot be undefined")

	let x = element.getBoundingClientRect().left
	let y = element.getBoundingClientRect().top
	let ww = Math.max(
		document.documentElement.clientWidth,
		window.innerWidth || 0
	)
	let hw = Math.max(
		document.documentElement.clientHeight,
		window.innerHeight || 0
	)
	let w = element.clientWidth
	let h = element.clientHeight
	return y < hw && y + h > 0 && x < ww && x + w > 0
}

export const getSetIntersection = (setA, setB) => {
	if (!(setA instanceof Set) || !(setB instanceof Set))
		throw new TypeError("setA and setB must be of type Set")
	let _intersection = new Set()
	for (let elem of setB) {
		if (setA.has(elem)) {
			_intersection.add(elem)
		}
	}
	return _intersection
}

export function fadeOut(el) {
	if (!(el instanceof Element))
		throw new TypeError("Argument 1 must be an Element")

	el.style.opacity = 1
	;(function fade() {
		if ((el.style.opacity -= 0.1) < 0) {
			el.style.display = "none"
		} else {
			requestAnimationFrame(fade)
		}
	})()
}

export function fadeIn(el, display) {
	if (!(el instanceof Element))
		throw new TypeError("Argument 1 must be an Element")

	el.style.opacity = 0
	el.style.display = display || "block"
	;(function fade() {
		var val = parseFloat(el.style.opacity)
		if (!((val += 0.1) > 1)) {
			el.style.opacity = val
			requestAnimationFrame(fade)
		}
	})()
}

export function stringIsValidDate(str) {
	const regexExp =
		/(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})/gi
	return regexExp.test(str)
}

export const getCompanyCookie = () => {
	return getCookie("company")
}

const getAge = (date) => {
	const dateString = date.split("-").reverse().join("-")
	const today = new Date()
	const birthDate = new Date(dateString)
	let age = today.getFullYear() - birthDate.getFullYear()
	const month = today.getMonth() - birthDate.getMonth()
	if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
		age--
	}

	// If the age is less then 1, return 1
	return age > 0 ? age : 1
}

export const getParticipantAges = () => {
	const companyDataCookie = getCookie("companyData")

	if (!companyDataCookie) return
	const participants = JSON.parse(companyDataCookie)
	if (!participants) {
		return []
	}
	const participantsAges = [18]
	if (participants.conjoint && participants.conjoint?.isSelected) {
		participantsAges.push(18)
	}
	if (participants.enfants) {
		participants.enfants.forEach(function (child) {
			if (!child.isSelected) {
				return
			}
			let age = getAge(child.dateNaissance)

			//if age over 18 I push in the age cookie a value of 18, as planning doesnt get value over 18 (either kids, under 18 or adult 18)
			if (age > 18) {
				age = 18
			}
			participantsAges.push(age)
		})
	}

	return participantsAges
}

export const getParticipantAgesFromSE = () => {
	const participantsAgesFromCookie = getCookie("participants-ages-no-encoding")
	const participantsAgesFromCookieArray = participantsAgesFromCookie
		?.split(",")
		.map(function (age) {
			return parseInt(age)
		})

	return participantsAgesFromCookieArray
}

export const getAdultsAmount = () => {
	const participantAges = getParticipantAges()
	const adultAges = participantAges.filter((age) => age >= 18)
	const adultsAmount = adultAges.length
	return adultsAmount
}

export const getParticipantsAmount = () => {
	const participantAmount = getParticipantAges().length
	return participantAmount
}

export const checkIfLocaleIsCSE = (locale) => {
	return locale === "fr_FR_CSE"
}

export const getCurrentLocale = () => {
	const hostname = window.location.hostname
	const obj = {
		"www.travelski.com": "fr_FR",
		"www.nl.travelski.com": "nl_NL",
		"www.uk.travelski.com": "en_US",
		"cse.travelski.com": "fr_FR_CSE",
	}
	
	return obj[hostname] || "fr_FR"
}

export const getLanguageCode=(locale)=>  {
	// Extract the first two letters of the locale and convert to uppercase
	switch (localeToUseForSkiSchool(locale).split('_')[0]) {
	  case 'fr':
		return 'FR';
	  case 'nl':
		return 'NL';
	  case 'en':
		return 'EN';
	  default:
		return 'EN'; // Default to English if locale is unrecognized
	}
  }

export const formatLocaleWithDash =(locale)=>  {
	switch (localeToUseForSkiSchool(locale).split('_')[0]) {
		case 'fr':
		return 'fr-FR';
		case 'nl':
		return 'nl-NL';
		case 'en':
		return 'en-UK';
		default:
		return 'en-UK'; // Default to English if locale is unrecognized
	}
}

export const checkIfCompanyIsCgos = () => {
	const currentComapny = getCompanyCookie()
	return Object.values(CgosCompaniesEnum).includes(currentComapny);
}

export const checkIfCompanyIsCgosExceptCgosAuraAndCgosPaca = () => {
	const currentComapny = getCompanyCookie()
	return Object.values(CgosCompaniesEnum).includes(currentComapny) && ![CompaniesEnum.CGOSAURA, CompaniesEnum.CGOSPACA].includes(currentComapny);
}

export const isSkiSchool = () => {
	const companyCookie = getCookie(CookiesEnum.COMPANY)
	const companyLangCookie = getCookie(CookiesEnum.COMPANY_LANG)
	return !!(companyCookie && companyLangCookie === 'nl')
}

export const localeToUseForSkiSchool = (locale) => {
	return isSkiSchool() ? Locales.NL : locale
}

export const isABtest = () => {
	const cookieABtest = getCookie("hidePromoAndCrossPrice")
	const isABtest = cookieABtest ? true : false
	return isABtest
}

export const getPreDefinedSeasonDates = () => {
	// Rule: Summer (ETE): 2nd saturday of may AND Winter (Hiver): 1st saturtday of november
	const year = new Date().getFullYear()

	// get the 2nd Saturday of May
	const may = new Date(year, 4, 1)
	const secondSaturday = new Date(
		may.setDate(1 + ((6 - may.getDay() + 7) % 7) + 7)
	)
	const formattedMayDate = `${secondSaturday
		.getDate()
		.toString()
		.padStart(2, "0")}/${(secondSaturday.getMonth() + 1)
		.toString()
		.padStart(2, "0")}/${year}`

	// get the 1st Saturday of November
	const november = new Date(year, 10, 1)
	const firstSaturday = new Date(
		november.setDate(1 + ((6 - november.getDay() + 7) % 7))
	)
	const formattedNovemberDate = `${firstSaturday
		.getDate()
		.toString()
		.padStart(2, "0")}/${(firstSaturday.getMonth() + 1)
		.toString()
		.padStart(2, "0")}/${year}`

	const seasonDates = []
	seasonDates.beginingOfSummer = formattedMayDate
	seasonDates.beginingOfWinter = formattedNovemberDate

	return seasonDates
}

export const seasonConfigFallBack = () => {
	const winter = [10, 11, 12, 1, 2, 3, 4]
	const summer = [5, 6, 7, 8, 9]
	return {
		winter,
		summer,
	}
}

export const getSasonBasedOnDate = (date) => {
    // Accept YYYY-MM-DD or YYYY/MM/DD ─ split on either "-" or "/"
    const [year, month, day] = date.split(/[-/]/).map(Number);
    const selectedDate = new Date(year, month - 1, day); // month is 0-based in JS
    
    const defaultSeasonsDates = getPreDefinedSeasonDates();
    const [summerDay, summerMonth, summerYear] = defaultSeasonsDates.beginingOfSummer.split('/').map(Number);
    const [winterDay, winterMonth, winterYear] = defaultSeasonsDates.beginingOfWinter.split('/').map(Number);
    
    const summerStartDate = new Date(summerYear, summerMonth - 1, summerDay);
    const winterStartDate = new Date(winterYear, winterMonth - 1, winterDay);

    const isSelectedDateInSummerRange = selectedDate >= summerStartDate && selectedDate < winterStartDate;
    return isSelectedDateInSummerRange ? "E" : "H";
}

const startOfSummerDateService = (dateStart) => {
	const dateOfBooking = new Date(dateStart)
	const yearOfBooking = dateOfBooking.getFullYear()

	const mayMonthNumber = 4 // months are zero-based in JavaScript, so May is 4
	const firstDayOfMonth = new Date(yearOfBooking, mayMonthNumber, 1)
	const firstSaturday = 7 - firstDayOfMonth.getDay()
	const targetSaturday = firstSaturday + 7

	const startDateOfSummer = new Date(
		yearOfBooking,
		mayMonthNumber,
		targetSaturday
	)
	return startDateOfSummer
}

const startOfWinterDateService = (dateStart) => {
	const dateOfBooking = new Date(dateStart)
	const yearOfBooking = dateOfBooking.getFullYear()

	const novemberMonthNumber = 10 // months are zero-based in JavaScript, so November is 10
	const firstDayOfMonth = new Date(yearOfBooking, novemberMonthNumber, 1)
	const firstSaturday = 7 - firstDayOfMonth.getDay()
	const startDateOfWinter = new Date(
		yearOfBooking,
		novemberMonthNumber,
		firstSaturday
	)

	return startDateOfWinter
}

export const checkIsSummerBookingService = (dateStart) => {
	const startDateOfSummer = startOfSummerDateService(dateStart)
	const startDateOfWinter = startOfWinterDateService(dateStart)

	const dateOfBooking = new Date(dateStart)
	// If the date of the booking is bigger or the same and as the summer dates, and before the start of the winter, it is a summer booking
	if (dateOfBooking >= startDateOfSummer && dateOfBooking < startDateOfWinter) {
		return true
	}
}

// Winter season starts 1st Saturday of November
export const checkIsWinterBookingService = (dateStart) => {
	const startDateOfSummer = startOfSummerDateService(dateStart)
	const startDateOfWinter = startOfWinterDateService(dateStart)
	const dateOfBooking = new Date(dateStart)
	// If the date of the booking is before the start of the winter, and before the start of the summer season, the booking is still a winter season booking
	// If the date of booking is more then the startDateOfWinter then it's still a winter season booking
	if (
		(dateOfBooking <= startDateOfWinter && dateOfBooking < startDateOfSummer) ||
		dateOfBooking >= startDateOfWinter
	) {
		return true
	}
}

export const checkIsSummerOrWinter = (dateStart) => {
	if (!dateStart)
		throw new Error("dateStart is mandatory in checkIsSummerOrWinter")
	const isSummer = checkIsSummerBookingService(dateStart)
	const isWinter = checkIsWinterBookingService(dateStart)
	if (isSummer) {
		return "E"
	} else if (isWinter) {
		return "H"
	}
}

export const formatDate = (date) => {
	const day = date.split("/")[0]
	const month = date.split("/")[1]
	const year = date.split("/")[2]
	return `${year + "-" + month + "-" + day}`
}

export const isMobile =() => {
	return window?.screenSizeManager?.screenType =='mobile' ? true : false
}

/**
 * Checks if the given response is empty (either null, undefined, or an empty object/array).
 * 
 * @param {Object|Array} response - The response to check.
 * @returns {boolean} - True if the response is empty, otherwise false.
 */
export const isEmptyResponse = (response) => !response || (Array.isArray(response) ? response.length === 0 : Object.keys(response).length === 0);

export const calculateAverage = (valueOne, valueTwo) =>{
    const value1 = valueOne || 0;
    const value2 = valueTwo || 0;
    return (value1 + value2) / 2;
}


export const calculateAverageRating = (packOne, packTwo) =>{
    const rating1 = packOne.averageRatingPerLihe || 0;
    const rating2 = packTwo.averageRatingPerLihe || 0;
    return (rating1 + rating2) / 2;
}

export const calculateAverageReview = (packOne, packTwo)=> {
    const reviews1 = packOne.reviewNumberPerLihe || 0;
    const reviews2 = packTwo.reviewNumberPerLihe || 0;
    return Math.ceil((reviews1 + reviews2) / 2);
}